import { err, ok, ResultAsync } from "neverthrow";

export type FetchError<E = unknown> = NetworkError | HttpError<E>;

export type FetchJsonError<E = unknown> = FetchError<E> | ParseError;

export interface NetworkError {
  type: "network";
  error: Error;
}

export interface HttpError<E = unknown> {
  type: "http";
  url: string;
  status: number;
  headers: Headers;
  json?: E;
}

export interface ParseError {
  type: "json-parse";
  message: string;
}

export function safeFetch<E = unknown>(
  input: URL | string,
  init?: RequestInit,
  fetcher: typeof globalThis.fetch = globalThis.fetch,
): ResultAsync<Response, FetchError<E>> {
  return ResultAsync.fromPromise(
    fetcher(input, init),
    (error): NetworkError => ({
      type: "network",
      error: error instanceof Error ? error : new Error(String(error)),
    }),
  ).andThen((response) => {
    if (!response.ok) {
      return ResultAsync.fromSafePromise(
        response.json().catch(() => null),
      ).andThen((json) => {
        const error: HttpError<E> = {
          type: "http" as const,
          url: input.toString(),
          status: response.status,
          headers: response.headers,
          json: json as E | undefined,
        };
        return err(error);
      });
    }
    return ok(response);
  });
}

export function safeFetchJson<T = unknown, E = unknown>(
  input: URL | string,
  init?: RequestInit,
  fetcher: typeof globalThis.fetch = globalThis.fetch,
): ResultAsync<T, FetchJsonError<E>> {
  return safeFetch<E>(input, init, fetcher).andThen((response) =>
    ResultAsync.fromPromise(
      response.json() as Promise<T>,
      (error): ParseError => {
        if (error instanceof SyntaxError) {
          return {
            type: "json-parse",
            message: error.message,
          };
        }
        // should never happen:
        throw new Error(
          `Unexpected error while parsing JSON: ${String(error)}`,
        );
      },
    ),
  );
}

export function safeFetchText<E = unknown>(
  input: URL | string,
  init?: RequestInit,
  fetcher: typeof globalThis.fetch = globalThis.fetch,
): ResultAsync<string, FetchError> {
  return safeFetch<E>(input, init, fetcher).andThen((response) =>
    ResultAsync.fromSafePromise(response.text()),
  );
}
